import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getStoredUser } from "@/lib/auth";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { useTenant } from "@/contexts/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { Play, Search, ListFilter } from "lucide-react";
import { CollapsibleSearch } from "@/components/ui/collapsible-search";

import MultiSelectFilter from "@/components/leads/MultiSelectFilter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";


function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function RunOutcomeBadge({ disposition }) {
  const d = String(disposition || "").trim();
  if (!d) return null;

  const cls =
    d === "sold"
      ? "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-400/20"
      : d === "dead_deal"
        ? "bg-rose-500/15 text-rose-200 ring-1 ring-rose-400/20"
        : "bg-indigo-500/15 text-indigo-200 ring-1 ring-indigo-400/20";

  const label = d
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");

  return <Badge className={cls}>{label}</Badge>;
}

function RunRow({ run, selected, onSelect, onContinue }) {
  const prospects = run?.prospects || [];
  const prospectNames = prospects.map((p) => p.name).filter(Boolean);
  const leadLabel = run?.lead_full_name || run?.lead_id || "";
  const mainLabel = prospectNames.join(", ") || leadLabel || "Unassigned";

  return (
    <button
      className={
        "w-full rounded-xl border p-4 text-left ring-1 transition-colors " +
        (selected
          ? "border-indigo-400/30 bg-indigo-500/10 ring-indigo-400/20"
          : "border-white/10 bg-white/5 ring-white/10 hover:bg-white/10")
      }
      onClick={onSelect}
      data-testid={`runs-row-${run.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="truncate text-sm font-semibold text-slate-100">{mainLabel}</div>
            {!run?.lead_id ? (
              <Badge className="bg-amber-500/15 text-amber-200 ring-1 ring-amber-400/20">Unassigned</Badge>
            ) : null}
            <RunOutcomeBadge disposition={run.disposition} />
          </div>

          <div className="mt-2 space-y-1 text-xs text-slate-200/70">
            <div>
              <span className="text-slate-200/60">Script:</span> {run.script_name || run.script_id || "—"}
            </div>
            <div>
              <span className="text-slate-200/60">Lead:</span> {run.lead_full_name || run.lead_id || "Unassigned"}
            </div>
            <div>
              <span className="text-slate-200/60">Agent:</span> {run.user_name || run.user_id}
            </div>
            <div>
              <span className="text-slate-200/60">Ended:</span> {fmtWhen(run.ended_at || run.disposition_at)}
            </div>
            <div className="line-clamp-1">
              <span className="text-slate-200/60">Ended on:</span> {run.final_node_title || run.final_node_id || "—"}
            </div>
          </div>
        </div>

        {/* Resume button removed from list rows; use the details panel to resume. */}
      </div>
    </button>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-6 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-28 w-full" />
      <Skeleton className="h-28 w-full" />
    </div>
  );


}

export default function RunsPage() {
  usePageTitle("Runs");
  const nav = useNavigate();
  const user = getStoredUser();
  
  // P0 TENANT ISOLATION: Subscribe to tenant context for automatic refetch
  const { selectedTenantId } = useTenant();

  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const [detailsLoading, setDetailsLoading] = useState(false);
  const [details, setDetails] = useState(null);

  // Filters
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [q, setQ] = useState("");
  const [dispositionFilter, setDispositionFilter] = useState([]);
  const [scriptFilter, setScriptFilter] = useState([]);
  const [agentFilter, setAgentFilter] = useState([]);

  // Assign lead (for unassigned runs)
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignQuery, setAssignQuery] = useState("");
  const [assignResults, setAssignResults] = useState([]);
  const [assignSelectedId, setAssignSelectedId] = useState(null);
  const [assignSearching, setAssignSearching] = useState(false);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [limit, setLimit] = useState(25);

  const dispositions = useMemo(() => {
    const set = new Set();
    (runs || []).forEach((r) => {
      if (r?.disposition) set.add(r.disposition);
    });
    return Array.from(set).sort();
  }, [runs]);

  const scripts = useMemo(() => {
    const set = new Set();
    (runs || []).forEach((r) => {
      const label = String(r?.script_name || r?.script_id || "").trim();
      if (label) set.add(label);
    });
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [runs]);

  const agents = useMemo(() => {
    const set = new Set();
    (runs || []).forEach((r) => {
      const label = String(r?.user_name || r?.user_id || "").trim();
      if (label) set.add(label);
    });
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [runs]);

  const notesForRun = useMemo(() => {
    if (!selectedId) return "";
    try {
      return localStorage.getItem(`missionassist_run_${selectedId}_notes`) || "";
    } catch {
      return "";
    }
  }, [selectedId]);

  const filteredRuns = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const dispoSet = new Set(dispositionFilter || []);
    const scriptSet = new Set(scriptFilter || []);
    const agentSet = new Set(agentFilter || []);

    const fromTs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toTs = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null;

    return (runs || []).filter((r) => {
      if (dispoSet.size > 0 && !dispoSet.has(String(r.disposition || ""))) return false;

      const scriptLabel = String(r.script_name || r.script_id || "");
      if (scriptSet.size > 0 && !scriptSet.has(scriptLabel)) return false;

      if (user?.role === "admin" || user?.role === "super_admin") {
        const agentLabel = String(r.user_name || r.user_id || "");
        if (agentSet.size > 0 && !agentSet.has(agentLabel)) return false;
      }

      if (fromTs || toTs) {
        const ts = r.ended_at || r.disposition_at || r.started_at;
        const t = ts ? new Date(ts).getTime() : null;
        if (!t) return false;
        if (fromTs && t < fromTs) return false;
        if (toTs && t > toTs) return false;
      }

      if (!needle) return true;

      const prospectNames = (r.prospects || []).map((p) => p.name).filter(Boolean).join(", ");
      const hay = [
        r.script_name,
        r.script_id,
        r.user_name,
        r.user_id,
        prospectNames,
        r.final_node_title,
        r.final_node_id,
        r.lead_full_name,
        r.lead_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(needle);
    });
  }, [runs, q, dispositionFilter, scriptFilter, agentFilter, dateFrom, dateTo, user?.role]);

  const selectedRun = useMemo(
    () => (filteredRuns || []).find((r) => r.id === selectedId) || null,


    [filteredRuns, selectedId],
  );

  const visibleRuns = useMemo(() => (filteredRuns || []).slice(0, limit), [filteredRuns, limit]);

  const fetchRuns = async () => {
    setLoading(true);
    try {
      const res = await api.get("/runs", {
        params: { completed: "true", exclude_sandbox: "true" },
      });
      const list = res.data || [];
      setRuns(list);
      setSelectedId((prev) => prev || list?.[0]?.id || null);
    } finally {
      setLoading(false);
    }
  };

  const fetchDetails = async (runId) => {
    if (!runId) return;
    setDetailsLoading(true);
    try {
      const res = await api.get(`/runs/${runId}`);
      setDetails(res.data);
    } finally {
      setDetailsLoading(false);
    }
  };

  const clearFilters = () => {
    setQ("");
    setDispositionFilter([]);
    setScriptFilter([]);
    setAgentFilter([]);
    setDateFrom("");
    setDateTo("");
  };

  useEffect(() => {
    if (!assignOpen) return;
    const q = String(assignQuery || "").trim();
    if (q.length < 2) {
      setAssignResults([]);
      return;
    }

    const t = setTimeout(async () => {
      setAssignSearching(true);
      try {
        const res = await api.get("/leads/search", { params: { q, limit: 10 } });
        setAssignResults(res.data?.items || []);
      } finally {
        setAssignSearching(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [assignOpen, assignQuery]);

  const submitAssignLead = async () => {
    const runId = details?.run?.id;
    if (!runId) return;

    try {
      let leadId = assignSelectedId;
      const leadName = String(assignQuery || "").trim();

      if (!leadId) {
        const lr = await api.post("/leads", { full_name: leadName || "Prospect" });
        leadId = lr.data?.id;
      }

      await api.post(`/runs/${runId}/link-lead`, { lead_id: leadId });
      toast({ title: "Lead assigned" });
      setAssignOpen(false);
      setAssignQuery("");
      setAssignSelectedId(null);
      setAssignResults([]);

      await fetchRuns();
      await fetchDetails(runId);
    } catch (e) {
      toast({
        title: "Could not assign lead",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };


  useEffect(() => {
    fetchRuns();
    // P0 TENANT ISOLATION: Refetch when tenant context changes
  }, [selectedTenantId]);

  useEffect(() => {
    if (!selectedId) {
      setDetails(null);
      return;
    }
    fetchDetails(selectedId);
  }, [selectedId]);

  const onContinue = (run) => {
    if (!run?.script_id) return;
    // Resume at the run's final node (per your decision) by passing run_id in query.
    nav(`/app/scripts/${run.script_id}/play?run_id=${run.id}`);
  };

  return (
    <div className="space-y-6" data-testid="runs-page">
      <div className="grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Card className="glass h-[calc(100vh-220px)] flex flex-col" data-testid="runs-list-card">
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="text-slate-100">Completed Runs</CardTitle>
                {/* subtitle removed */}
              </div>

              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                <CollapsibleSearch
                  value={q}
                  onChange={setQ}
                  placeholder="Search runs..."
                  width="w-[300px]"
                  data-testid="runs-search"
                />

                <button
                  type="button"
                  onClick={() => setFiltersOpen((v) => !v)}
                  className={`relative grid h-9 w-9 place-items-center rounded-lg ring-1 hover:bg-white/15 ${filtersOpen ? "bg-indigo-500/20 ring-indigo-400/30" : "bg-white/10 ring-white/15"}`}
                  aria-label="Open filters"
                  data-testid="runs-filter-toggle"
                >
                  <ListFilter className={`h-4 w-4 ${filtersOpen ? "text-indigo-400" : "text-slate-100"}`} />
                </button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 min-h-0 flex flex-col gap-4">
              {filtersOpen ? (
                <div className="mb-4 space-y-3" data-testid="runs-filters">

                  <div className="space-y-3 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <div className="text-xs text-slate-200/70">Date From</div>
                          <Input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="w-full bg-white/5 text-slate-100 ring-1 ring-white/10 sm:w-[170px]"
                          />
                        </div>
                        <div className="space-y-1">
                          <div className="text-xs text-slate-200/70">Date To</div>
                          <Input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="w-full bg-white/5 text-slate-100 ring-1 ring-white/10 sm:w-[170px]"
                          />
                        </div>
                      </div>

                      <Button
                        type="button"
                        variant="secondary"
                        onClick={clearFilters}
                        data-testid="runs-filter-clear"
                      >
                        Clear Filters
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <div className="space-y-1">
                        <div className="text-xs text-slate-200/70">Disposition</div>
                        <MultiSelectFilter options={dispositions} values={dispositionFilter} onChange={setDispositionFilter} />
                      </div>

                      <div className="space-y-1">
                        <div className="text-xs text-slate-200/70">Script</div>
                        <MultiSelectFilter options={scripts} values={scriptFilter} onChange={setScriptFilter} />
                      </div>

                      {(user?.role === "admin" || user?.role === "super_admin") ? (
                        <div className="space-y-1">
                          <div className="text-xs text-slate-200/70">Agent</div>
                          <MultiSelectFilter options={agents} values={agentFilter} onChange={setAgentFilter} />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="flex-1 min-h-0">
                {loading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-24 w-full" />
                  </div>
                ) : filteredRuns.length === 0 ? (
                  <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-sm text-slate-200/70">
                    No runs match the current filters.
                  </div>
                ) : (
                  <div className="h-full overflow-auto pr-2" data-testid="runs-scroll">
                    <div className="space-y-3">
                      {visibleRuns.map((r) => (
                        <RunRow
                          key={r.id}
                          run={r}
                          selected={r.id === selectedId}
                          onSelect={() => setSelectedId(r.id)}
                          onContinue={() => onContinue(r)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>


              {!loading ? (
                <div className="flex items-center justify-between text-xs text-slate-200/70">
                  <div data-testid="runs-filter-count">
                    Showing <span className="text-slate-100">{visibleRuns.length}</span> of {filteredRuns.length}
                    {filteredRuns.length !== runs.length ? ` (from ${runs.length})` : ""}
                  </div>

                  <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
                    <SelectTrigger className="h-9 w-[140px] bg-white/5 text-slate-100 ring-1 ring-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="glass border-white/10">
                      {[10, 25, 50, 100].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n} / Page
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-7">
          <Card className="glass h-[calc(100vh-220px)] flex flex-col" data-testid="runs-details-card">
            <CardHeader>
              <CardTitle className="text-slate-100">Run Details</CardTitle>
              <div className="mt-1 text-xs text-slate-200/70">
                {selectedRun ? "" : "Select a run to view details."}
              </div>
            </CardHeader>
            <CardContent className="flex-1 min-h-0 flex flex-col">
              {!selectedRun ? (
                <div className="flex-1 rounded-xl border border-white/10 bg-white/5 p-6 text-sm text-slate-200/70">
                  Select a run from the list.
                </div>
              ) : detailsLoading ? (
                <DetailSkeleton />
              ) : !details ? (
                <div className="flex-1 rounded-xl border border-white/10 bg-white/5 p-6 text-sm text-slate-200/70">
                  Could not load details.
                </div>
              ) : (
                <div className="flex-1 min-h-0 overflow-auto pr-2" data-testid="runs-details-scroll">
                  <div className="space-y-5" data-testid="runs-details">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-slate-200/70">
                        Script: <span className="text-slate-100">{details.run?.script_name || "Script"}</span>
                      </div>
                      <div className="mt-1 text-sm text-slate-200/70">
                        Agent: <span className="text-slate-100">{details.run?.user_name || details.run?.user_id}</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => onContinue(details.run)}
                      data-testid="runs-details-continue"
                    >
                      <Play className="mr-2 h-4 w-4" />
                      Resume Run
                    </Button>
                  </div>

                  <Separator className="bg-white/10" />


                  <div className="rounded-xl border border-white/10 bg-white/5 p-4" data-testid="runs-lead-card">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-slate-100">Lead</div>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setAssignOpen(true);
                          setAssignQuery(details.run?.lead_full_name || "");
                          setAssignSelectedId(details.run?.lead_id || null);
                          setAssignResults([]);
                        }}
                        data-testid="runs-assign-lead-open"
                      >
                        {details.run?.lead_id ? "Change" : "Assign"}
                      </Button>
                    </div>
                    <div className="mt-3 text-sm text-slate-100">
                      {details.run?.lead_full_name || details.run?.lead_id || "Unassigned"}
                    </div>
                  </div>


                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-xl border border-white/10 bg-white/5 p-4 sm:col-span-2">
                      <div className="text-sm font-semibold text-slate-100">Prospects</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(details.run?.prospects || []).length === 0 ? (
                          <div className="text-sm text-slate-200/70">—</div>
                        ) : (
                          (details.run?.prospects || []).map((p) => (
                            <Badge
                              key={p.id}
                              className="bg-white/10 text-slate-100 ring-1 ring-white/10"
                            >
                              {p.name}
                            </Badge>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                      <div className="text-sm font-semibold text-slate-100">Disposition</div>
                      <div className="mt-3">
                        <RunOutcomeBadge disposition={details.run?.disposition} />
                      </div>
                    </div>

                    <div className="row-span-2 rounded-xl border border-white/10 bg-white/5 p-4">
                      <div className="text-sm font-semibold text-slate-100">Started</div>
                      <div className="mt-3 text-sm text-slate-100">
                        {fmtWhen(details.run?.started_at)}
                      </div>

                      <div className="mt-5 text-sm font-semibold text-slate-100">Ended</div>
                      <div className="mt-3 text-sm text-slate-100">
                        {fmtWhen(details.run?.ended_at || details.run?.disposition_at)}
                      </div>
                    </div>

                    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                      <div className="text-sm font-semibold text-slate-100">Final Node</div>
                      <div className="mt-3 text-sm text-slate-100 line-clamp-2">
                        {details.run?.final_node_title || details.run?.final_node_id || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                      <div className="text-sm font-semibold text-slate-100">Captured Data</div>
                      <div className="mt-3 text-xs text-slate-200/70">
                        {Array.isArray(details.run_data) ? details.run_data.length : 0} entries
                      </div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                      <div className="text-sm font-semibold text-slate-100">Quote Sheet</div>
                      <div className="mt-3 text-xs text-slate-200/70">
                        {Array.isArray(details.quote_values) ? details.quote_values.length : 0} values
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-white/10 bg-white/5 p-4" data-testid="runs-notes-card">
                    <div className="text-sm font-semibold text-slate-100">Notes</div>
                    <div className="mt-3 text-sm text-slate-200/80 whitespace-pre-wrap" data-testid="runs-notes-text">
                      {notesForRun.trim() ? notesForRun : "—"}
                    </div>
                  </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>


      <Dialog
        open={assignOpen}
        onOpenChange={(v) => {
          setAssignOpen(v);
          if (!v) {
            setAssignQuery("");
            setAssignSelectedId(null);
            setAssignResults([]);
          }
        }}
      >
        <DialogContent className="glass border-white/10 max-w-xl" data-testid="assign-lead-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Assign Lead</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <Input
              value={assignQuery}
              onChange={(e) => {
                setAssignQuery(e.target.value);
                setAssignSelectedId(null);
              }}
              placeholder="Search lead by name (or type to create new)"
              className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
              data-testid="assign-lead-input"
            />

            <div className="rounded-xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
              <div className="border-b border-white/10 px-4 py-2 text-xs font-semibold text-slate-200/70">Matches</div>
              <div className="max-h-[240px] overflow-auto">
                {assignSearching ? (
                  <div className="px-4 py-3 text-sm text-slate-200/70">Searching…</div>
                ) : null}
                {!assignSearching && assignResults.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-slate-200/70">No matches.</div>
                ) : null}
                {assignResults.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className={
                      "w-full px-4 py-3 text-left text-sm hover:bg-white/10 " +
                      (r.id === assignSelectedId ? "bg-indigo-500/20" : "")
                    }
                    onClick={() => {
                      setAssignSelectedId(r.id);
                      setAssignQuery(r.label || r.full_name || "");
                    }}
                    data-testid={`assign-lead-result-${r.id}`}
                  >
                    <div className="text-slate-100">{r.label || r.full_name || "Lead"}</div>
                    <div className="text-xs text-slate-200/60">{r.id}</div>
                  </button>
                ))}
              </div>
            </div>

            {!assignSelectedId && String(assignQuery || "").trim() ? (
              <div className="text-xs text-slate-200/70">We’ll create a new lead named: <span className="text-slate-100">{assignQuery.trim()}</span></div>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setAssignOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!assignSelectedId && !String(assignQuery || "").trim()}
              onClick={submitAssignLead}
              data-testid="assign-lead-submit"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
